import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import './ShowcaseHomePage.css';

const processSteps = [
  {
    index: '01',
    title: 'Shape the logic',
    text: 'Build behavior trees visually and keep logic readable.',
  },
  {
    index: '02',
    title: 'Tune the details',
    text: 'Adjust parameters and edge cases in one workspace.',
  },
  {
    index: '03',
    title: 'Export what you need',
    text: 'Generate project files and hand off with less friction.',
  },
];

const qualities = [
  {
    title: 'Clean signal',
    detail: 'A focused canvas helps teams understand a project quickly.',
  },
  {
    title: 'Fast iteration',
    detail: 'Less context switching means more time testing ideas.',
  },
  {
    title: 'Real collaboration',
    detail: 'Designers and engineers stay aligned on the same project state.',
  },
];

function ShowcaseHomePage() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setScrollY(window.scrollY || 0);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  const drift = Math.min(scrollY * 0.1, 80);

  return (
    <div className="home-shell" style={{ '--scroll-drift': `${drift}px` }}>
      <header className="home-hero layout-container" data-reveal>
        <div className="home-hero__copy">
          <p className="home-kicker">Origami Workspace</p>
          <h1>Design robot behavior clearly.</h1>
          <p className="home-hero__lead">
            A visual workspace for building automation logic and reviewing edge cases.
          </p>
          <div className="home-actions">
            <Link to="/marketplace" className="home-btn home-btn--solid">
              Explore marketplace
            </Link>
            <Link to="/about" className="home-btn home-btn--outline">
              About Origami
            </Link>
          </div>
        </div>
      </header>

      <section className="home-section layout-container">
        <div className="home-section__heading" data-reveal>
          <p className="home-kicker">How it flows</p>
          <h2>A straightforward path from idea to output</h2>
        </div>
        <div className="home-process">
          {processSteps.map((step) => (
            <article key={step.index} className="home-process__card" data-reveal>
              <span>{step.index}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section layout-container">
        <div className="home-grid">
          <article className="home-feature home-feature--large" data-reveal>
            <p className="home-kicker">Why teams choose it</p>
            <h2>Built for focus, not busywork.</h2>
            <p>The interface stays calm and structured as projects grow.</p>
            <Link to="/signup" className="home-btn home-btn--solid home-btn--compact">
              Create account
            </Link>
          </article>
          <div className="home-quality-list">
            {qualities.map((item) => (
              <article key={item.title} className="home-feature" data-reveal>
                <h3>{item.title}</h3>
                <p>{item.detail}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="home-callout" data-reveal>
        <div className="layout-container home-callout__inner">
          <h2>Ready to start your next project?</h2>
          <div className="home-actions">
            <Link to="/marketplace" className="home-btn home-btn--solid">
              Browse projects
            </Link>
            <Link to="/login" className="home-btn home-btn--outline">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      <footer className="home-footer">
        <div className="layout-container">
          <p>(c) {new Date().getFullYear()} Origami Robotics</p>
        </div>
      </footer>
    </div>
  );
}

export default ShowcaseHomePage;
